// transaction.service.ts - Fixed version with proper Bluebird typing
import { finalize } from "rxjs/operators";
import { Injectable } from '@angular/core';
import { SocketService } from './socket.service';
import { v4 as uuidv4 } from 'uuid';
import * as Bluebird from 'bluebird';

export interface SingleTargetTransactionResult {
  channel: string;
  result: any;
  promise: Bluebird<any>;
  results: any[];
}

@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  source: any;
  settled: any;
  success: any;
  progress: any;
  error: any;
  data: any[] = [];
  lastData: any;
  body: any;

  results: any[] = [];

  constructor(
    private socketService: SocketService
  ) { }

  createChannel(): string {
    return 'tx.' + uuidv4();
  }

  MultiTargetTransaction(targets: any[], options: any): Bluebird<any[]> {
    let pending = Object.create(null);
    this.results = [];
    let channel = this.createChannel();

    const doneListener = (someChannel: string, data: any) => {
      if (someChannel === channel) {
        pending[data.source].done(data);
      }
    };

    const progressListener = (someChannel: string, data: any) => {
      if (someChannel === channel) {
        pending[data.source].onProgress(data);
      }
    };

    const cancelListener = (someChannel: string, data: any) => {
      if (someChannel === channel) {
        Object.keys(pending).forEach(source => {
          pending[source].cancel(data);
        });
      }
    };

    this.socketService.socket.on('tx.done', doneListener);
    this.socketService.socket.on('tx.progress', progressListener);
    this.socketService.socket.on('tx.cancel', cancelListener);

    const promise = targets.map((target) => {
      let result = new options.result(target);
      let pendingResult = new PendingTransactionResult(result);
      pending[options.id ? target[options.id] : target.id] = pendingResult;
      this.results.push(result);
      return pendingResult.promise;
    });

    const cleanup = () => {
      this.socketService.socket.removeListener('tx.done', doneListener);
      this.socketService.socket.removeListener('tx.progress', progressListener);
      this.socketService.socket.removeListener('tx.cancel', cancelListener);
      this.socketService.socket.emit('tx.cleanup', channel);
    };

    return (Bluebird as any).allSettled(promise)
      .finally(() => cleanup())
      .then(() => this.results);
  }

  SingleTargetTransaction(target: any, options: any): SingleTargetTransactionResult {
    let result = new options.result(target);
    let pending = new PendingTransactionResult(result);
    let channel = this.createChannel();

    const doneListener = (someChannel: string, data: any) => {
      if (someChannel === channel) {
        pending.done(data);
      }
    };

    const progressListener = (someChannel: string, data: any) => {
      if (someChannel === channel) {
        pending.onProgress(data);
      }
    };

    const cancelListener = (someChannel: string, data: any) => {
      if (someChannel === channel) {
        pending.cancel(data);
      }
    };

    this.socketService.socket.on('tx.done', doneListener);
    this.socketService.socket.on('tx.progress', progressListener);
    this.socketService.socket.on('tx.cancel', cancelListener);

    this.results = [result];

    const cleanup = () => {
      this.socketService.socket.removeListener('tx.done', doneListener);
      this.socketService.socket.removeListener('tx.progress', progressListener);
      this.socketService.socket.removeListener('tx.cancel', cancelListener);
      this.socketService.socket.emit('tx.cleanup', channel);
    };

    // Attach cleanup
    pending.promise.finally(() => cleanup());

    // Return a structured object that contains all the properties we need
    return {
      channel: channel,
      result: result,
      promise: pending.promise, // Return original promise for progress
      results: this.results
    };
  }

  create(target: any | any[], options?: any): SingleTargetTransactionResult | Bluebird<any[]> {
    if (options && !options.result) {
      options.result = TransactionResult;
    }

    if (Array.isArray(target)) {
      return this.MultiTargetTransaction(target, options || {
        result: DeviceTransactionResult,
        id: 'serial'
      });
    } else {
      return this.SingleTargetTransaction(target, options || {
        result: DeviceTransactionResult,
        id: 'serial'
      });
    }
  }

  // punch(channel: string): Bluebird<string> {
  //   // Use the proper Bluebird defer typing
  //   const resolver = Bluebird.defer<string>();

  //   // Define the listener with proper this-binding
  //   const punchListener = (someChannel: string) => {
  //     if (channel === someChannel) {
  //       resolver.resolve(channel);
  //     }
  //   };

  //   this.socketService.socket.on('tx.punch', punchListener);
  //   this.socketService.socket.emit('tx.punch', channel);

  //   // Store this reference for the finally function
  //   const socketService = this.socketService;

  //   return resolver.promise
  //     .timeout(5000) // TypeScript should now recognize this method
  //     .finalize(function () {
  //       socketService.socket.removeListener('tx.punch', punchListener);
  //     });
  // }
}

export class TransactionResult {
  source: any;
  settled: any;
  success: any;
  progress: any;
  error: any = null;
  data: any[] = [];
  lastData: any = null;
  body: any = null;

  constructor(source: any) {
    this.source = source;
  }
}

export class DeviceTransactionResult extends TransactionResult {
  device: any;

  constructor(device: any) {
    super(device);
    this.device = this.source;
  }
}

export class TransactionError extends Error {
  code: string;
  override name: string = 'TransactionError';

  constructor(result: any) {
    super(result.error);
    this.code = this.message = result.error;
    // Error.captureStackTrace(this, TransactionError);
  }
}

export class PendingTransactionResult {
  result: TransactionResult;
  promise: Bluebird<any>;
  onProgress: (message: any) => void; // Renamed from progress to avoid conflict
  done: (message: any) => void;
  cancel: (message: any) => void;

  constructor(result: any) {
    // Create a deferred object to support native progress callbacks
    const deferred = (Bluebird as any).defer();
    const promise = deferred.promise;

    let seq = 0;
    let last = Infinity;
    const unplaced: any[] = [];

    const readQueue = () => {
      let message;
      let foundAny = false;

      while (seq <= last && (message = unplaced[seq])) {
        unplaced[seq] = undefined;

        if (seq === last) {
          result.success = message.success;

          if (message.body) {
            result.body = JSON.parse(message.body);
          }

          if (result.success) {
            if (message.data) {
              result.lastData = result.data[seq] = message.data;
            }
            result.settled = true;
            result.progress = 100;
            deferred.resolve(result);
          } else {
            result.lastData = result.error = message.data;
            result.settled = true;
            result.progress = 100;
            deferred.reject(new TransactionError(result));
          }

          return;
        } else {
          if (message.progress !== undefined) { // Fix: Allow 0 progress
            result.progress = message.progress;
          }
        }

        foundAny = true;
        result.lastData = result.data[seq++] = message.data;
      }

      if (foundAny) {
        // Trigger the manual progress mechanism
        progressHandlers.forEach(handler => handler(result));
      }
    };

    const progressHandlers: Array<(data: any) => void> = [];

    this.onProgress = (message) => {
      unplaced[message.seq] = message;
      readQueue();
    };

    this.done = (message) => {
      last = message.seq;
      unplaced[message.seq] = message;
      readQueue();
    };

    this.cancel = (message) => {
      if (!result.settled) {
        last = message.seq = seq;
        unplaced[message.seq] = message;
        readQueue();
      }
    };

    this.result = result;
    this.promise = promise as any;

    // Manually implement .progress() on the promise instance
    (this.promise as any).progress = (this.promise as any).progressed = (handler: (data: any) => void) => {
      progressHandlers.push(handler);
      return this.promise;
    };
  }
}